import { z } from "zod";

import { OPS_CATEGORY, OPS_MFA_TYPE, OPS_OWNER, OPS_STATUS } from "../common";

const categoryValues = Object.values(OPS_CATEGORY) as [string, ...string[]];
const ownerValues = Object.values(OPS_OWNER) as [string, ...string[]];
const statusValues = Object.values(OPS_STATUS) as [string, ...string[]];
const mfaTypeValues = Object.values(OPS_MFA_TYPE) as [string, ...string[]];

const optionalText = z.string().optional().or(z.literal(""));

const websiteField = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine(
    (val) => !val || /^https?:\/\//.test(val),
    "Website must start with http:// or https://",
  );

export const opsCredentialsFormSchema = z
  .object({
    practiceId: z.string().min(1, "A practice must be selected"),
    category: z.enum(categoryValues, {
      required_error: "A category is required",
    }),
    owner: z.enum(ownerValues).optional().or(z.literal("")),
    status: z.enum(statusValues).default(OPS_STATUS.ACTIVE),
    name: z.string().min(1, "A name is required"),
    targetName: optionalText,
    website: websiteField,
    username: optionalText,
    password: optionalText,
    accountHolderName: optionalText,
    accountHolderDob: optionalText,
    mfaType: z.enum(mfaTypeValues).default(OPS_MFA_TYPE.NONE),
    mfaEmail: z
      .string()
      .email("Must be a valid email address")
      .optional()
      .or(z.literal("")),
    mfaPhone: optionalText,
    totpSecret: optionalText,
    securityQuestions: z
      .array(z.string())
      .max(3, "At most 3 security questions")
      .default([]),
    securityAnswers: z
      .array(z.string())
      .max(3, "At most 3 security questions")
      .default([]),
    notes: optionalText,
  })
  .superRefine((data, ctx) => {
    if (data.mfaType === OPS_MFA_TYPE.EMAIL && !data.mfaEmail?.trim()) {
      ctx.addIssue({
        path: ["mfaEmail"],
        code: z.ZodIssueCode.custom,
        message: "An MFA email is required for the Email method",
      });
    }
    if (data.mfaType === OPS_MFA_TYPE.SMS && !data.mfaPhone?.trim()) {
      ctx.addIssue({
        path: ["mfaPhone"],
        code: z.ZodIssueCode.custom,
        message: "An MFA phone is required for the SMS method",
      });
    }
    if (
      data.mfaType === OPS_MFA_TYPE.AUTHENTICATOR &&
      !data.totpSecret?.trim()
    ) {
      ctx.addIssue({
        path: ["totpSecret"],
        code: z.ZodIssueCode.custom,
        message: "A TOTP secret is required for the Authenticator method",
      });
    }
    data.securityQuestions.forEach((question, index) => {
      if (question.trim() && !data.securityAnswers[index]?.trim()) {
        ctx.addIssue({
          path: ["securityAnswers", index],
          code: z.ZodIssueCode.custom,
          message: "An answer is required for each security question",
        });
      }
    });
  });

export type OpsCredentialsFormFields = z.infer<typeof opsCredentialsFormSchema>;

export type OpsCredentialPayload = {
  category: string;
  owner?: string | null;
  status?: string;
  name: string;
  targetName?: string | null;
  website?: string | null;
  username?: string | null;
  password?: string | null;
  accountHolderName?: string | null;
  accountHolderDob?: string | null;
  mfaType?: string | null;
  mfaEmail?: string | null;
  mfaPhone?: string | null;
  totpSecret?: string | null;
  securityQuestions?: string[] | null;
  securityAnswers?: string[] | null;
  notes?: string | null;
};
